import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, Observable, map, shareReplay, finalize } from 'rxjs';
import { AuthService } from 'auth';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService?: AuthService;
  private refreshInProgress$: Observable<string> | null = null;

  // Routes where an unauthenticated user is expected — never force-redirect from these.
  private readonly publicRoutePaths = [
    '/login',
    '/account/activate',
    '/account/reset-password',
    '/set-password',
    '/auth/forgot-password',
    '/auth/verify-otp',
  ];

  constructor(
    private injector: Injector,
    private router: Router,
  ) { }

  private isOnPublicRoute(): boolean {
    const path = window.location.pathname.toLowerCase();
    return this.publicRoutePaths.some((route) => path.includes(route));
  }

  // Coalesces concurrent 401s onto a single in-flight refresh call instead of
  // firing one refresh-token request per failed request (which races against
  // refresh-token rotation and can log the user out even after a successful refresh).
  private getRefreshedToken(): Observable<string> {
    if (!this.refreshInProgress$) {
      this.refreshInProgress$ = this.authService!.refreshToken().pipe(
        map((res: { accessToken?: string; refreshToken?: string; data?: { token?: string; refreshToken?: string } }) => {
          const newAccessToken = res.accessToken || res.data?.token;
          const newRefreshToken = res.refreshToken || res.data?.refreshToken;
          if (!newAccessToken) {
            throw new Error('No access token in refresh response');
          }
          this.authService!.saveTokens(newAccessToken, newRefreshToken ?? '');
          return newAccessToken;
        }),
        shareReplay(1),
        finalize(() => {
          this.refreshInProgress$ = null;
        }),
      );
    }
    return this.refreshInProgress$;
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    const accessToken = this.authService.getAccessToken();
    let authReq = req;
    const url = req.url.toLowerCase();
    const authenticatedAuthRoutes = [
      '/authentication/logout',
      '/authentication/change-password/confirm',
      '/authentication/change-password',
    ];
    const isPublicAuthRequest =
      url.includes('/authentication/') &&
      !authenticatedAuthRoutes.some((route) => url.includes(route));

    if (accessToken && !isPublicAuthRequest) {
      authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    }

    return next.handle(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && url.includes('/authentication/logout')) {
          return throwError(() => err);
        }

        // On public/pre-auth pages, a 401 is expected (no session yet).
        // Don't attempt refresh or force-navigate away from these routes.
        if (err.status === 401 && this.isOnPublicRoute()) {
          return throwError(() => err);
        }

        if (err.status === 401) {
          return this.getRefreshedToken().pipe(
            switchMap((newAccessToken) => {
              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newAccessToken}`,
                },
              });
              return next.handle(retryReq);
            }),
            catchError(() => {
              this.authService!.clearTokens();
              this.router.navigate(['/login']);
              return throwError(() => err);
            }),
          );
        }

        return throwError(() => err);
      }),
    );
  }
}